'use client'

import { CrossIcon } from '@repo/ui/CrossIcon'
import { LargeUserIcon } from '@repo/ui/LargeUserIcon'
import { NewClientForm } from '@repo/ui/NewClientForm'
import { NewClientNumberEntry } from '@repo/ui/NewClientNumberEntry'
import { newClientSchema } from '@repo/validation/schema_NewClient'
import { TextLinkButton } from '@repo/ui/TextLinkButton'
import { useNewClientStore } from '@repo/store/useNewClientStore'
import { useNewMatterStore } from '@repo/store/useNewMatterStore'
import { useState, useRef, useEffect } from 'react'
import { useTheme } from '@mui/material/styles'
import { useToastStore } from '@repo/store/useToastStore'
import { validate } from '@repo/validation/validation'
import { WizardFooter } from '@repo/ui/WizardFooter'
import { WizardFormSection } from '@repo/ui/WizardFormSection'
import { WizardIconTitle } from '@repo/ui/WizardIconTitle'
import { WizardPanel } from '@repo/ui/WizardPanel'
import { WizardPrompt } from '@repo/ui/WizardPrompt'
import MUIStack from '@mui/material/Stack'
import PropTypes from 'prop-types'

export const NewClientDetails = ({
  updateStepState,
  wideMode,
  handlePrevNext,
  testData = null,
  wizardId,
  currentStep
}) => {
  const { showToast } = useToastStore()
  const {
    newClientNo,
    highRisk,
    billingAddressee,
    billingHouse,
    billingArea,
    billingCounty,
    billingPostalTown,
    billingPostcode,
    namedClients,
    existingClient,
    updateState,
    resetWizardClientState
  } = useNewClientStore()
  const { resetWizardMatterState } = useNewMatterStore()
  const theme = useTheme()
  const formRef = useRef()
  const [validationErrors, setValidationErrors] = useState({})
  const [stateToSave, setStateToSave] = useState({})

  useEffect(() => {
    setStateToSave({
      highRisk,
      billingAddressee,
      billingHouse,
      billingArea,
      billingCounty,
      billingPostalTown,
      billingPostcode,
      namedClients
    })
  }, [
    highRisk,
    billingAddressee,
    billingHouse,
    billingArea,
    billingCounty,
    billingPostalTown,
    billingPostcode,
    namedClients
  ])

  const handleClickBack = async () => {
    await resetWizardClientState()
    await resetWizardMatterState()

    window.location.href.includes('?wizard=NewMatter-NewClient-NotificationApproval&stage=client&step=client-details')
      ? handlePrevNext(-2)
      : handlePrevNext(-1)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setValidationErrors({})

    try {
      await validate(newClientSchema, stateToSave)
    } catch (err) {
      const { inner } = err
      const errors = {}
      let firstErrorKey
      inner.forEach(({ path, message }, index) => {
        errors[path] = message
        if (index === 0) firstErrorKey = CSS.escape(path)
      })

      console.log(errors)

      showToast({ type: 'error', text: 'Please check all fields for errors', title: 'Form errors' })

      // scroll to error if possible
      const container = document.querySelector('[data-testid="WizardStepContentsContainer"]')
      const errorElement = firstErrorKey
        ? document.querySelector(`[data-testid="WizardPanel"]:has([name=${firstErrorKey}])`)
        : null
      if (container && errorElement) container.scrollTop = errorElement.offsetParent?.offsetTop || 0

      setValidationErrors(errors)
      return
    }

    const statusText = namedClients.length === 1 ? '1 named client added' : `${namedClients.length} named clients added`
    updateStepState({ status: 'completed', statusText })
    handlePrevNext(1)
  }

  const addNamedClient = () => {
    updateState({
      key: 'namedClients',
      value: [
        ...namedClients,
        existingClient
          ? existingClient
          : {
              id: new Date().getTime(),
              clientType: 'I',
              title: '',
              forename: '',
              initials: '',
              surname: '',
              emailAddress: '',
              house: '',
              area: '',
              postalTown: '',
              county: '',
              postcode: ''
            }
      ]
    })
  }

  useEffect(() => {
    if (!namedClients?.length) addNamedClient()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namedClients])

  const removeNamedClient = (index) => {
    updateState({
      key: 'namedClients',
      value: namedClients.filter((_, i) => i !== index)
    })
  }

  const updateNamedClient = ({
    id,
    clientType,
    title,
    forename,
    initials,
    surname,
    emailAddress,
    house,
    area,
    postalTown,
    county,
    postcode,
    index
  }) => {
    namedClients[index] = {
      id,
      clientType,
      title,
      forename,
      initials,
      surname,
      emailAddress,
      house,
      area,
      postalTown,
      county,
      postcode
    }

    updateState({
      key: 'namedClients',
      value: namedClients
    })
  }

  return (
    <form onSubmit={handleSubmit} ref={formRef}>
      {currentStep?.index === 0 ? (
        <WizardPrompt wizardId={wizardId} screenFunction="create a new client and matter" />
      ) : null}

      <MUIStack direction="column" gap={8}>
        <WizardPanel data-testid="NewClientDetails">
          <WizardFormSection sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <WizardIconTitle
              title="Client details"
              icon={LargeUserIcon}
              sx={{ mb: 1, width: '50%' }}
              text="Enter details of each named client"
            />

            <NewClientNumberEntry
              defaultValue={newClientNo || ''}
              onChange={(value) => updateState({ key: 'newClientNo', value })}
              surname={namedClients && namedClients[0] ? namedClients[0].surname : ''}
              size="medium"
              sx={{ width: '240px', mt: 4 }}
              testMode={testData}
            />
          </WizardFormSection>
        </WizardPanel>

        {namedClients?.map((client, index) => {
          if (!client) return null
          return (
            <NewClientForm
              key={`${client.id || `${client.emailAddress}-${index}`}`}
              id={`${client.id || `${client.emailAddress}-${index}`}`}
              index={index}
              defaultValue={client}
              onChange={updateNamedClient}
              onRemove={removeNamedClient}
              validationErrors={validationErrors}
              testMode={testData}
            />
          )
        })}

        <TextLinkButton
          onClick={addNamedClient}
          sx={{ mt: 1, ml: 5, width: '300px', mb: 8 }}
          title={
            <MUIStack direction="row" alignItems="center" gap={2}>
              <CrossIcon
                sx={{
                  transform: 'rotate(45deg)',
                  width: '24px',
                  height: '24px',
                  color: theme?.palette?.secondary?.dark || 'black'
                }}
              />
              Add additional named client
            </MUIStack>
          }
        />

        <WizardFooter
          wideMode={wideMode}
          handleClickBack={handleClickBack}
          continueButtonIsFormSubmitButton
          backButtonVisible={currentStep?.index !== 0}
          continueButtonDisabled={!newClientNo}
        />
      </MUIStack>
    </form>
  )
}

NewClientDetails.propTypes = {
  updateStepState: PropTypes.func,
  currentStep: PropTypes.object,
  wideMode: PropTypes.bool,
  handlePrevNext: PropTypes.func,
  testData: PropTypes.array,
  wizardId: PropTypes.string
}
